import React, { useContext } from 'react';
import styled from 'styled-components';
import Layout from './HLayout';

import CartProducts from './CartProducts';
import { CartContext } from './contexts/CartContext';
import { formatNumber } from '../utils';
import { Link } from 'react-router-dom';

const HCart = () => {

    const { total, cartItems, itemCount, clearCart, checkout, clearCheckout, tax, tprice } = useContext(CartContext);

    return (
        <Layout title="Cart" description="This is the Cart page">
            <CartPage>
                <Heading>
                    Your cart
                    {itemCount > 0 && (
                        <Count>{itemCount} item{itemCount > 1 ? 's' : ''}</Count>
                    )}
                </Heading>

                {(() => {
                    const subtotalCents = parseFloat(total) || 0;
                    const thresholdCents = 5000;
                    const remainingCents = Math.max(0, thresholdCents - subtotalCents);
                    const progress = Math.min(100, (subtotalCents / thresholdCents) * 100);
                    
                    return (
                        <ProgressBarContainer>
                            <ProgressText>
                                {remainingCents > 0 ? (
                                    <>You're only <strong>{formatNumber(remainingCents)}</strong> away from <strong>FREE Shipping!</strong></>
                                ) : (
                                    <>✨ Congratulations! You've unlocked <strong>FREE Shipping!</strong></>
                                )}
                            </ProgressText>
                            <ProgressTrack>
                                <ProgressFill $progress={progress} $isFree={remainingCents === 0} />
                            </ProgressTrack>
                        </ProgressBarContainer>
                    );
                })()}

                {cartItems.length > 0 ? (
                    <CartGrid>
                        <ItemsCol>
                            <CartProducts />
                        </ItemsCol>

                        <SummaryCol>
                            <SummaryCard>
                                <SummaryTitle>Order summary</SummaryTitle>

                                <Row>
                                    <span>Items</span>
                                    <strong>{formatNumber(total)}</strong>
                                </Row>
                                <Divider />
                                <Row>
                                    <span>Total tax</span>
                                    <strong>{formatNumber(tax)}</strong>
                                </Row>
                                <Divider />
                                <Row className="grand">
                                    <span>Total payment</span>
                                    <strong>{formatNumber(tprice)}</strong>
                                </Row>

                                <CheckoutBtn to="/HPayList">Go to checkout</CheckoutBtn>
                                <ClearBtn type="button" onClick={clearCart}>Clear cart</ClearBtn>
                            </SummaryCard>
                        </SummaryCol>
                    </CartGrid>
                ) : (
                    <Empty>Your cart is empty</Empty>
                )}

                {checkout && (
                    <Success>
                        <p>Checkout successful <span role="img" aria-label="celebration">🎉</span></p>
                        <Link to="/" onClick={clearCheckout}>Buy more</Link>
                    </Success>
                )}
            </CartPage>
        </Layout>
    );
}

export default HCart;

/* ---------- styles ---------- */
const CartPage = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  padding: 24px 14px 48px;

  @media screen and (min-width: 480px) {
    padding: 32px 20px 64px;
  }
`;

const Heading = styled.h1`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin: 0 0 24px;
  font-size: clamp(1.6rem, 3.5vw, 2.2rem);
  font-weight: 800;
  color: #1f2d3d;
`;

const Count = styled.span`
  background: rgba(1, 191, 113, 0.12);
  color: #0a8f57;
  font-size: 0.9rem;
  font-weight: 700;
  padding: 5px 12px;
  border-radius: 999px;
`;

const ProgressBarContainer = styled.div`
  margin-bottom: 24px;
  background: #fff;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid rgba(31, 45, 61, 0.08);
  box-shadow: 0 4px 12px rgba(31, 45, 61, 0.04);
`;

const ProgressText = styled.div`
  font-size: 0.95rem;
  color: #1f2d3d;
  margin-bottom: 12px;
  text-align: center;
  
  strong {
    color: #0a8f57;
    font-weight: 800;
  }
`;

const ProgressTrack = styled.div`
  height: 8px;
  background: rgba(31, 45, 61, 0.08);
  border-radius: 999px;
  overflow: hidden;
  width: 100%;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.$progress}%;
  background: ${props => props.$isFree ? '#01bf71' : 'linear-gradient(90deg, #ff9a3c, #01bf71)'};
  border-radius: 999px;
  transition: width 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), background 0.4s ease;
`;

const CartGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media screen and (min-width: 900px) {
    grid-template-columns: minmax(0, 1fr) 340px;
    align-items: start;
  }
`;

const ItemsCol = styled.div`
  min-width: 0;
`;

const SummaryCol = styled.div`
  @media screen and (min-width: 900px) {
    position: sticky;
    top: 100px;
  }
`;

const SummaryCard = styled.div`
  background: #fff;
  border: 1px solid rgba(31, 45, 61, 0.08);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 10px 26px rgba(31, 45, 61, 0.07);
`;

const SummaryTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 1.1rem;
  font-weight: 800;
  color: #1f2d3d;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  color: #556575;
  font-size: 0.98rem;

  strong {
    color: #1f2d3d;
    font-weight: 700;
  }

  &.grand {
    font-size: 1.1rem;
    strong {
      color: #0a8f57;
      font-size: 1.25rem;
      font-weight: 800;
    }
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(31, 45, 61, 0.1);
  margin: 14px 0;
`;

const CheckoutBtn = styled(Link)`
  display: block;
  margin-top: 22px;
  text-align: center;
  text-decoration: none;
  background: #01bf71;
  color: #fff;
  font-weight: 700;
  padding: 14px;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(1, 191, 113, 0.3);
  transition: background 0.15s ease, transform 0.15s ease;

  &:hover {
    background: #04a763;
    color: #fff;
    transform: translateY(-1px);
  }
`;

const ClearBtn = styled.button`
  display: block;
  width: 100%;
  margin-top: 10px;
  padding: 12px;
  background: transparent;
  border: 1px solid rgba(31, 45, 61, 0.15);
  border-radius: 12px;
  color: #6a7b8a;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: rgba(239, 83, 80, 0.08);
    border-color: rgba(239, 83, 80, 0.4);
    color: #ef5350;
  }
`;

const Empty = styled.div`
  text-align: center;
  color: #8a99a8;
  font-size: 1.1rem;
  padding: 60px 20px;
  background: #fff;
  border: 1px dashed rgba(31, 45, 61, 0.15);
  border-radius: 16px;
`;

const Success = styled.div`
  margin-top: 24px;
  text-align: center;
  color: #0a8f57;

  a {
    display: inline-block;
    margin-top: 8px;
    color: #01bf71;
    font-weight: 700;
    text-decoration: none;
  }
`;
